import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Bell, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import Reveal from "@/components/site/Reveal";
import {
  SleepySlothThumb,
  CosmicCatThumb,
  PixelPicnicThumb,
} from "@/components/site/GameThumbnails";
import NotifyForm from "@/components/site/NotifyForm";
import { mediaUrl } from "@/lib/api";
import { useAppPages } from "@/contexts/AppPagesContext";

const DEFAULT_THUMBS = {
  "sleepy-sloth": SleepySlothThumb,
  "cosmic-cat": CosmicCatThumb,
  "pixel-picnic": PixelPicnicThumb,
};

const FallbackThumb = ({ title }) => (
  <div className="relative w-full bg-gradient-to-br from-[#1A1C20] to-[#0F0F18] flex items-center justify-center" style={{ aspectRatio: "320/200" }}>
    <div className="absolute inset-0 pf-charcoal-glow opacity-50" />
    <span className="pf-display text-2xl text-[#F35A1F]/80">{title || "Coming Soon"}</span>
  </div>
);

export const Portfolio = ({ content }) => {
  const c = content || {};
  const cards = c.cards || [];
  const appOptions = cards.map((c) => c.title).filter(Boolean);
  const { pages } = useAppPages();
  const pageSlugs = new Set(pages.map((p) => p.slug));

  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyInitial, setNotifyInitial] = useState("");
  const [notifySession, setNotifySession] = useState(0);

  const openNotify = (appTitle = "") => {
    setNotifyInitial(appTitle);
    setNotifySession((s) => s + 1);
    setNotifyOpen(true);
  };

  return (
    <>
      <section
        id="portfolio"
        data-testid="portfolio-section"
        className="relative pf-section bg-[#121316] text-[#FBFAF8] overflow-hidden"
      >
        <div className="pointer-events-none absolute inset-0 pf-charcoal-glow" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-0 pf-noise" aria-hidden="true" />

        <div className="pf-container relative">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <Reveal>
              <div>
                <div className="flex items-center gap-2">
                  <span className="pf-mono text-[11px] uppercase tracking-wider text-[#F35A1F]">
                    {c.eyebrow}
                  </span>
                  <span className="h-px w-10 bg-white/15" />
                </div>
                <h2 className="mt-3 pf-display text-[32px] sm:text-[44px] leading-tight tracking-tight">
                  {c.title_line1}
                  <br />
                  <span className="text-[#F35A1F]">{c.title_line2}</span>
                </h2>
                <p className="mt-3 max-w-xl text-white/70">{c.description}</p>
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <button
                type="button"
                onClick={() => openNotify("")}
                data-testid="portfolio-follow-cta"
                className="inline-flex items-center gap-2 self-start rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-sm font-medium text-white hover:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-[#121316]"
              >
                <Sparkles className="h-4 w-4 text-[#F35A1F]" />
                {c.follow_cta_label}
                <Bell className="h-4 w-4" />
              </button>
            </Reveal>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {cards.map((card, i) => {
              const SvgThumb = DEFAULT_THUMBS[card.id];
              const customUrl = card.image_id ? mediaUrl(card.image_id) : null;
              return (
                <Reveal key={card.id || i} delay={0.04 * i}>
                  <article
                    data-testid={`portfolio-card-${card.id}`}
                    className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[#1A1C20] transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-white/20"
                  >
                    <div className="relative">
                      {customUrl ? (
                        <img
                          src={customUrl}
                          alt={`${card.title} thumbnail`}
                          className="block w-full h-auto object-cover"
                          style={{ aspectRatio: "320/200" }}
                        />
                      ) : SvgThumb ? (
                        <SvgThumb className="w-full h-auto" />
                      ) : (
                        <FallbackThumb title={card.title} />
                      )}
                      {card.coming_soon !== false && (
                        <Badge
                          data-testid={`portfolio-card-${card.id}-badge`}
                          className="absolute right-3 top-3 rounded-full bg-white/10 border border-white/15 backdrop-blur text-[11px] tracking-wide text-white hover:bg-white/10"
                        >
                          Coming Soon
                        </Badge>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center justify-between">
                        <p className="pf-mono text-[10px] uppercase tracking-wider text-[#F35A1F]">
                          {card.tag}
                        </p>
                        <p className="pf-mono text-[10px] uppercase tracking-wider text-white/50">
                          ETA · {card.eta}
                        </p>
                      </div>
                      <h3 className="mt-2 pf-display text-[20px] text-white">{card.title}</h3>
                      <p className="mt-2 text-[14px] text-white/70 leading-relaxed">{card.desc}</p>
                      <div className="mt-4 flex items-center justify-between">
                        <div className="flex -space-x-1">
                          <span className="h-2 w-2 rounded-full bg-[#F35A1F]" />
                          <span className="h-2 w-2 rounded-full bg-white/40" />
                          <span className="h-2 w-2 rounded-full bg-white/20" />
                        </div>
                        {pageSlugs.has(card.id) ? (
                          <Link
                            to={`/apps/${card.id}`}
                            data-testid={`portfolio-card-${card.id}-view-page`}
                            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] font-semibold text-[#F35A1F] hover:text-white hover:bg-[#F35A1F]/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1A1C20]"
                          >
                            View page
                            <ArrowRight className="h-3.5 w-3.5" />
                          </Link>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openNotify(card.title)}
                            data-testid={`portfolio-card-${card.id}-notify`}
                            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] text-white/70 hover:text-white hover:bg-white/[0.06] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1A1C20]"
                          >
                            <Bell className="h-3.5 w-3.5" />
                            Notify me
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <NotifyForm
        key={notifySession}
        open={notifyOpen}
        onOpenChange={setNotifyOpen}
        initialAppInterest={notifyInitial}
        appOptions={appOptions}
      />
    </>
  );
};

export default Portfolio;
